import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, GlobeIcon, ShieldCheckIcon, SparklesIcon, Share2Icon, BarChart3Icon, SearchIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { StepVisual } from './approach/StepVisuals';
import { approachIntro, approachSteps, trustLayer, type ApproachStep, type ApproachVisualId } from '../data/approachSteps';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
const STEP_ICONS: Record<ApproachVisualId, typeof GlobeIcon> = {
  entrypoints: GlobeIcon,
  identity: ShieldCheckIcon,
  atna: SparklesIcon,
  systems: Share2Icon,
  action: BarChart3Icon
};
const TRUST_ICONS = [SearchIcon, SparklesIcon, ShieldCheckIcon, CheckIcon];

/** Faint dot field used on the indigo card and the bottom panel. */
const DOT_FIELD: React.CSSProperties = {
  backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px), radial-gradient(currentColor 1px, transparent 1px)',
  backgroundSize: '18px 18px, 26px 26px',
  backgroundPosition: '0 0, 9px 13px'
};

/** Circular connector that overlaps the edges of two adjacent cards. */
function CardConnector() {
  return <div className="hidden lg:flex items-center justify-center shrink-0 -mx-4 z-10 self-center" aria-hidden="true" data-id="element-504" data-mp="2lx3h5dj1i6wo">
      <span className="w-8 h-8 rounded-full bg-white border border-[#E4E6EC] shadow-[0_1px_3px_rgba(18,18,18,0.08)] flex items-center justify-center" data-id="element-505" data-mp="2bxcnfe2azvxn">
        <ArrowRightIcon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={2} data-id="element-506" data-mp="1y797t5xxj0h6" />
      </span>
    </div>;
}
function StepCard({
  step,
  isFR,
  index
}: {
  step: ApproachStep;
  isFR: boolean;
  index: number;
}) {
  const pick = (value: {
    en: string;
    fr: string;
  }) => isFR ? value.fr : value.en;
  const featured = Boolean(step.featured);
  const Icon = STEP_ICONS[step.visual];
  return <motion.article {...reveal} transition={{
    duration: 0.6,
    delay: index * 0.06,
    ease: EASE
  }} className={`relative flex-1 min-w-0 flex flex-col overflow-hidden rounded-2xl p-5 ${featured ? 'bg-gradient-to-br from-[#4F46E5] to-[#3A31C4] text-white shadow-[0_12px_32px_rgba(79,70,229,0.22)] lg:-my-2 lg:py-7' : 'border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)]'}`} data-id="element-507" data-mp="1a39jtjrn2812">
      {featured && <span className="pointer-events-none absolute -bottom-10 -right-10 w-52 h-52 rounded-full text-white/25 opacity-40" style={DOT_FIELD} aria-hidden="true" data-id="element-508" data-mp="1o2j16mrkz86m" />}

      <div className="relative" data-id="element-509" data-mp="1e2idypqlc7x5">
        {/* Number + icon badge */}
        <div className="flex items-start justify-between gap-3" data-id="element-510" data-mp="7nr6u4avjquo">
          <div data-id="element-511" data-mp="7l53kgv0nxi9">
            <div className={`font-sans text-xl ${featured ? 'text-white' : 'text-[#4F46E5]'}`} data-id="element-512" data-mp="2o4ury6s8gng">
              {step.num}
            </div>
            <span className={`block w-5 h-px mt-1 ${featured ? 'bg-white/50' : 'bg-[#4F46E5]'}`} aria-hidden="true" data-id="element-513" data-mp="2edbd6lq78ix6" />
          </div>
          <span className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${featured ? 'bg-white/15' : 'bg-[#4F46E5]/[0.07]'}`} data-id="element-514" data-mp="2o8mr51ts9m3v">
            <Icon className={`w-[1.15rem] h-[1.15rem] ${featured ? 'text-white' : 'text-[#4F46E5]'}`} strokeWidth={1.75} aria-hidden="true" data-id="element-515" data-mp="2xyfuxgnoowxk" />
          </span>
        </div>

        <h3 className={`font-sans text-[9.5px] font-semibold tracking-[0.13em] uppercase leading-relaxed mt-6 ${featured ? 'text-white/85' : 'text-[#5B6478]'}`} data-id="element-516" data-mp="370yr5hwysbau">
          {pick(step.label)}
        </h3>
        <p className={`font-serif text-[1.4rem] leading-[1.18] mt-2 ${featured ? 'text-white' : 'text-[#121212]'}`} data-id="element-517" data-mp="1hkpkzt9vveg0">
          {pick(step.headline)}
        </p>

        <ul className="mt-4 space-y-1" data-id="element-518" data-mp="333rsbkvmxns1">
          {step.items.map(item => <li key={item.en} className={`flex items-center gap-2.5 font-sans text-sm ${featured ? 'text-white/90' : 'text-[#121212]/75'}`} data-id="element-519" data-mp="3h2rdujnupmjz">
              <span className={`w-1 h-1 rounded-full shrink-0 ${featured ? 'bg-white/70' : 'bg-[#4F46E5]'}`} aria-hidden="true" data-id="element-520" data-mp="2pbbtyxdoidxg" />
              {pick(item)}
            </li>)}
        </ul>
      </div>

      <div className="relative mt-auto pt-6" data-id="element-521" data-mp="2tdov18hhdnnw">
        <StepVisual visual={step.visual} isFR={isFR} data-id="element-522" data-mp="2tbvso5dwhplp" />
      </div>
    </motion.article>;
}
export function Approach() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: {
    en: string;
    fr: string;
  }) => isFR ? value.fr : value.en;
  return <section className="bg-white border-t border-[#E4E6EC] py-16 lg:py-20 px-4 sm:px-6" data-id="element-523" data-mp="1rhastegkpnh7">
      <div className="max-w-[1320px] mx-auto" data-id="element-524" data-mp="1w9arz4otkccq">
        {/* Header */}
        <motion.div {...reveal} className="max-w-3xl mx-auto text-center" data-id="element-525" data-mp="1hkejj6iuoxbl">
          <div className="flex items-center justify-center gap-3" aria-hidden="true" data-id="element-526" data-mp="1hi1uk9eodmp1">
            <span className="w-6 h-px bg-[#4F46E5]/40" data-id="element-527" data-mp="2a93bog1xhsb7" />
            <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#4F46E5] uppercase" aria-hidden="false" data-id="element-528" data-mp="2a9d6cgxx4nn8">
              {pick(approachIntro.eyebrow)}
            </p>
            <span className="w-6 h-px bg-[#4F46E5]/40" data-id="element-529" data-mp="20d09xbaznvos" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.14] tracking-tight text-[#121212] mt-4" data-id="element-530" data-mp="2585vua4e5acv">
            <span className="block" data-id="element-531" data-mp="ou5y95t0pwss">{pick(approachIntro.headlineLead)}</span>
            <span className="block" data-id="element-532" data-mp="tvd4v1jfcpxv">
              {pick(approachIntro.headlineBefore)}
              <span className="text-[#4F46E5]" data-id="element-533" data-mp="2wg7g3wbx07gi">
                {pick(approachIntro.headlineAccent)}
              </span>
              {pick(approachIntro.headlineAfter)}
            </span>
          </h2>
          <p className="font-sans text-base md:text-lg text-[#5B6478] leading-relaxed mt-4" data-id="element-534" data-mp="2mq4oo0kfz1ka">
            {pick(approachIntro.intro)}
          </p>
        </motion.div>

        {/* Five connected steps — one row from lg up */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-none lg:flex lg:items-stretch gap-4 lg:gap-0 mt-12" data-id="element-535" data-mp="2cukvvn16cizr">
          {approachSteps.map((step, index) => <React.Fragment key={step.num} data-id="element-536" data-mp="23tcx73uqxg55" data-mpi={index}>
              {index > 0 && <CardConnector data-id="element-537" data-mp="1ytqj6s73fvva" />}
              <StepCard step={step} isFR={isFR} index={index} data-id="element-538" data-mp="1yuies30oxtlk" />
            </React.Fragment>)}
        </div>

        {/* ATNA trust layer */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="relative overflow-hidden mt-5 rounded-2xl bg-[#4F46E5]/[0.045] px-6 py-9 lg:px-12" data-id="element-539" data-mp="1yuf9xxfbwtq">
          <span className="pointer-events-none absolute -left-16 -bottom-16 w-72 h-72 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-540" data-mp="9kg2bs4nnjq1" />
          <span className="pointer-events-none absolute -right-16 -top-16 w-72 h-72 rounded-full text-[#4F46E5]/30 opacity-30" style={DOT_FIELD} aria-hidden="true" data-id="element-541" data-mp="jasofos4bkya" />

          <div className="relative text-center max-w-2xl mx-auto" data-id="element-542" data-mp="3msax1yfpsvzr">
            <p className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase" data-id="element-543" data-mp="3rqm2lwyz8kc6">
              {pick(trustLayer.eyebrow)}
            </p>
            <h3 className="font-serif text-2xl md:text-[1.9rem] text-[#121212] leading-snug mt-3" data-id="element-544" data-mp="lcmcx0sbkfr">
              {pick(trustLayer.headlineBefore)}
              <span className="text-[#4F46E5]" data-id="element-545" data-mp="1byoqza4dc72z">
                {pick(trustLayer.headlineAccent)}
              </span>
            </h3>
          </div>

          <ol className="relative grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-start gap-6 lg:gap-2 mt-9" data-id="element-546" data-mp="1x3ilqimv6fus">
            {trustLayer.steps.map((step, index) => {
            const Icon = TRUST_ICONS[index];
            const isLast = index === trustLayer.steps.length - 1;
            return <React.Fragment key={step.title.en} data-id="element-547" data-mp="2208z2y7aaxci" data-mpi={index}>
                  {index > 0 && <div className="hidden lg:flex items-center shrink-0 pt-5" aria-hidden="true" data-id="element-548" data-mp="2frxaez9plhsf">
                      <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-549" data-mp="2pqv5kl1n49ke" />
                    </div>}
                  <li className="flex-1 min-w-0 text-center" data-id="element-550" data-mp="vey8ockfibhy">
                    <span className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${isLast ? 'bg-[#4F46E5]' : 'bg-white shadow-[0_1px_3px_rgba(18,18,18,0.08)]'}`} data-id="element-551" data-mp="qf24odbqek2e">
                      <Icon className={`w-5 h-5 ${isLast ? 'text-white' : 'text-[#4F46E5]'}`} strokeWidth={isLast ? 2.75 : 1.75} aria-hidden="true" data-id="element-552" data-mp="2sbb0ojn1cd88" />
                    </span>
                    <div className="font-serif text-lg text-[#121212] mt-3" data-id="element-553" data-mp="2oahd5rdi90fv">
                      {pick(step.title)}
                    </div>
                    <p className="font-sans text-sm text-[#5B6478] leading-snug mt-1" data-id="element-554" data-mp="29erwm8r39fij">
                      {pick(step.caption)}
                    </p>
                  </li>
                </React.Fragment>;
          })}
          </ol>
        </motion.div>
      </div>
    </section>;
}